import { TransactionAPI, GLAPI, JournalAPI } from "@/apis/accounting";
import { InvAdjustmentOrderAPI } from "@/apis/material";
import { Currency } from "@/components/Numeric";
import { EnumLabel } from "@/components/Enum";
import { GLActionDef, GLImpactType } from "@/enums/common";
import { Button, Drawer, Grid, Link, Typography, Table } from "@arco-design/web-react";
import { useState } from "react";
import { formatDate } from "@/utils";

const Row = Grid.Row;
const Col = Grid.Col;

const AccountTransactionLink = (props) => {
    const { accountId, startDate, endDate } = props;

    const [visible, setVisible] = useState(false);

    const [transactionData, setTransactionData] = useState([]);
    const [transactionPagination, setTransactionPagination] = useState();

    const [glData, setGLData] = useState([]);
    const [glPagination, setGLPagination] = useState();

    const [journalEntryData, setJournalEntryData] = useState([]);
    const [journalEntryPagination, setJournalEntryPagination] = useState();

    const [adjustmentData, setAdjustmentData] = useState([]);
    const [adjustmentPagination, setAdjustmentPagination] = useState();

    const getTransactions = async (page) => {
        const values = await TransactionAPI.query(page, { accountIds: [accountId], startDate: startDate, endDate: endDate });
        setTransactionData(values.records);
        setTransactionPagination((pagination) => ({ ...pagination, current: page, total: values.total }));
    }

    const getGL = async (page) => {
        const values = await GLAPI.query(page, null, null, { accountId: accountId, startDate: startDate, endDate: endDate })
        setGLData(values.records);
        setGLPagination((pagination) => ({ ...pagination, current: page, total: values.total }));
    }

    const getJournalEntries = async (page) => {
        const values = await JournalAPI.details(page, { accountId: accountId, startDate: startDate, endDate: endDate })
        setJournalEntryData(values.records);
        setJournalEntryPagination((pagination) => ({ ...pagination, current: page, total: values.total }));
    }

    const getAdjustments = async (page) => {
        const values = await InvAdjustmentOrderAPI.query({ accountId: accountId, startDate: startDate, endDate: endDate, page });
        setAdjustmentData(values.records);
        setAdjustmentPagination((pagination) => ({ ...pagination, current: page, total: values.total }));
    }

    const glOrderUrl = (gl) => {
        switch (gl.impactType) {
            case GLImpactType.Receipt:
                return `/workspace/purchasing/receipt/${gl.impactFrom}`;
            case GLImpactType.Bill:
                return `/workspace/purchasing/bills/${gl.impactFrom}`;
            case GLImpactType.SOFulfillment:
                return `/workspace/sales/so-fulfillment/${gl.impactFrom}`;
            case GLImpactType.Invoice:
                return `/workspace/sales/invoice/${gl.impactFrom}`;
            case GLImpactType.WoFulfillment:
                return `/workspace/manufacturing/wo-fulfillment/${gl.impactFrom}`;
            case GLImpactType.InventoryAdjustment:
                return `/workspace/material/inventory_adjustment/${gl.impactFrom}`;
            default:
                return null;
        }
    }

    const openGLOrder = (gl) => {
        const w = window.open('_blank');
        w.location.href = glOrderUrl(gl);
    }

    const getDetails = async () => {
        getGL(1);
        getTransactions();
        getJournalEntries(1);
        getAdjustments(1);
    };

    return (
        <>
            <Link onClick={() => {
                setVisible(!visible);
            }}>{props.children}</Link>
            <Drawer visible={visible} width={800} title="Account Log"
                footer={
                    <Button type="primary" onClick={() => setVisible(false)}>Close</Button>
                }
                afterOpen={() => getDetails()}
                onCancel={() => setVisible(false)}>
                <Row>
                    <Col span={24}>
                        <Typography.Title heading={6}>GL Impact</Typography.Title>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Table columns={[
                            { title: "Date", dataIndex: "impactDate" },
                            {
                                title: "Order#", render: (col, record) => {
                                    const url = glOrderUrl(record);
                                    return url
                                        ? <Link onClick={() => openGLOrder(record)}>{record.orderNo}</Link>
                                        : record.orderNo;
                                }
                            },
                            { title: "Item", dataIndex: "itemName" },
                            { title: "Action", render: (col, record) => <EnumLabel enumObj={GLActionDef} value={record.action} /> },
                            {
                                title: "Amount", render: (col, record) =>
                                    <Currency value={record.totalAmount} />
                            },
                        ]}
                            data={glData} pagination={glPagination} rowKey="id"
                            onChange={(pagination) => {
                                getGL(pagination.current);
                            }} />
                    </Col>
                </Row>
                <Row style={{ marginTop: 15 }}>
                    <Col span={24}>
                        <Typography.Title heading={6}>Transactions</Typography.Title>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Table columns={[
                            { title: "Date", dataIndex: "date" },
                            { title: "Description", dataIndex: "description" },
                            {
                                title: "Amount", render: (col, record) =>
                                    <Currency value={record.amount} />
                            },
                        ]}
                            data={transactionData} pagination={transactionPagination} rowKey="id"
                            onChange={(pagination) => {
                                getTransactions(pagination.current);
                            }} />
                    </Col>
                </Row>
                <Row style={{ marginTop: 15 }}>
                    <Col span={24}>
                        <Typography.Title heading={6}>Journal Entries</Typography.Title>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Table columns={[
                            { title: "Date", dataIndex: "date" },
                            {
                                title: "Debit", render: (col, record) =>
                                    <Currency value={record.debit} />
                            },
                            {
                                title: "Credit", render: (col, record) =>
                                    <Currency value={record.credit} />
                            },
                        ]}
                            data={journalEntryData} pagination={journalEntryPagination} rowKey="id"
                            onChange={(pagination) => {
                                getJournalEntries(pagination.current);
                            }} />
                    </Col>
                </Row>
                <Row style={{ marginTop: 15 }}>
                    <Col span={24}>
                        <Typography.Title heading={6}>Inventory Adjustments</Typography.Title>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Table columns={[
                            {
                                title: "Date", dataIndex: "createTime",
                                render: (val) => val ? formatDate(val) : ""
                            },
                            { title: "ID", dataIndex: "adjustmentNo" },
                            {
                                title: "Amount", dataIndex: "adjustmentValue",
                                render: (val) => val != null ? <Currency value={val} /> : ""
                            },
                        ]}
                            data={adjustmentData} pagination={adjustmentPagination} rowKey="id"
                            onChange={(pagination) => {
                                getAdjustments(pagination.current);
                            }} />
                    </Col>
                </Row>
            </Drawer>
        </>
    )
}

export default AccountTransactionLink;